import { useEffect, useState, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getBookingDetails } from "../services/adminApiService/venueManagerApi/venueManagerApi";

import QRCode from "react-qr-code";
import axios from "axios";
import Swal from "sweetalert2";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  ArrowLeft,
  ChevronRight,
  Download,
  MapPin,
  Printer,
  CircleCheck,
  CircleX,
} from "lucide-react";
import "./styles.css";
import PaymentPopup from "./paymentPopUp";

const DEFAULT_AMOUNT = 1200;

const displayValue = (value, fallback) => {
  if (value === 0) return "0";
  if (value === null || value === undefined || value === "") return fallback;

  return value;
};

const formatDateParts = (value) => {
  if (!value) return null;

  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return null;

  return {
    day: date.toLocaleDateString("en-GB", { day: "numeric" }),
    shortMonth: date.toLocaleDateString("en-GB", { month: "short" }),
    longMonth: date.toLocaleDateString("en-GB", { month: "long" }),
    year: date.toLocaleDateString("en-GB", { year: "numeric" }),
  };
};
const getStatusStyle = (status) => {
  const isCompleted = status === "Completed";
  const isVerified = status === "Verified";
  const isRejected = status === "Rejected";
  const isCanceled = status === "Canceled";

  return {
    label: isCompleted
      ? "Successful"
      : isVerified
        ? "Verified"
        : isRejected
          ? "Rejected"
          : isCanceled
            ? "Canceled"
            : "Failed",

    bg: isCompleted || isVerified ? "#eaf5ec" : "#f8d7da",

    color: isCompleted || isVerified ? "#2e7d32" : "#a10000",
  };
};

const getPaymentStatus = (bookingStatus) => {
  const successStatuses = ["Completed", "Verified", "Rejected", "Canceled"];

  const isPending = bookingStatus === "Pending";
  const isSuccess = successStatuses.includes(bookingStatus);

  return {
    label: isPending ? "Pending" : isSuccess ? "Successful" : "Failed",

    bg: isPending ? "#FFF7E6" : isSuccess ? "#EAF5EC" : "#F8D7DA",

    color: isPending ? "#B26A00" : isSuccess ? "#2E7D32" : "#A10000",
  };
};

export default function BookingInformation() {
  const navigate = useNavigate();
  const location = useLocation();
  const pathSegments = location.pathname.split("/");
  const bookingId = pathSegments[pathSegments.length - 1];

  const [userData, setUserData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ticketDownloading, setTicketDownloading] = useState(false);
  const [qrSrc, setQrSrc] = useState("");
  const [openPaymentPopUp, setOpenPaymentPopup] = useState(false);

  const fetchBookingDetails = () => {
    if (bookingId) {
      getBookingDetails(bookingId || location.state?.id)
        .then((response) => {
          setUserData(response?.data?.data);
        })
        .catch((error) => {
          console.error("Error fetching booking details:", error);
        });
    }
  };

  useEffect(() => {
    fetchBookingDetails();
  }, [bookingId]);

  const bookingInfo = useMemo(() => {
    if (!userData) return {};

    return {
      // ✅ Venue
      venueName: userData?.venue?.venue_name || "",
      address: userData?.venue?.venue_address || "",
      directionsUrl: `https://www.google.com/maps?q=${userData?.venue?.coordinates?.latitude},${userData?.venue?.coordinates?.longitude}`,

      // ✅ User
      name: `${userData?.user?.first_name || ""} ${userData?.user?.last_name || ""}`,
      email: userData?.user?.email || "",
      contactNumber: userData?.contact_no || userData?.user?.mobile || "",
      avatar: userData?.indivisul_doc?.doc_file || "/logo/user.png",

      // ✅ Booking
      bookingId: userData?.booking_id || "",
      date: userData?.slot_from_date || "",
      shortDate: userData?.slot_from_date || "",

      // ✅ Time Slot
      timeSlot: `${userData?.slot_from_time} - ${userData?.slot_to_time}`,

      // ✅ Sport & Zone
      sport: userData?.sport?.sport_name || "",
      fieldOfPlay: userData?.zone?.zone_name || "",

      // ✅ Others
      participants: userData?.participants || "",
      amount: `₹${userData?.total_ammount || 0}`,

      // ✅ Status
      paymentStatus: userData?.payment_status,

      bookingStatus: userData?.booking_status || "",
      qr_code_link: userData?.qr_code_link || "",
      venue_image: userData?.venue?.image_url || "/logo/Play_Ground.png",
      remark_by: userData?.remark_by || "",
    };
  }, [userData]);

  const handleDownloadInvoice = async () => {
    const token = localStorage.getItem("access_token_admin");
    try {
      setLoading(true); // Optional: if you want to show the spinner while downloading
      const ticketId = bookingId;

      // Make sure this matches your actual backend endpoint route!
      // If your backend base URL is already set in axiosBookingInstance,
      // you might just need `/ticket/${ticketId}`
      const ticketBaseUrl = process.env.REACT_APP_VENUE_BASE_URL?.replace(
        /\/$/,
        "",
      );
      const endpoint = `${ticketBaseUrl}/invoice/${ticketId}`;

      const response = await axios.get(endpoint, {
        headers: {
          Authorization: token, // Uses the token already grabbed from localStorage
        },
        responseType: "blob", // CRITICAL: This tells axios to expect a file, not JSON
      });

      // Create a temporary URL for the downloaded file blob
      const url = window.URL.createObjectURL(new Blob([response.data]));

      // Create a hidden link, click it, and remove it
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `Invoice_${bookingId || "Ticket"}.pdf`);
      document.body.appendChild(link);
      link.click();

      // Clean up memory
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error downloading ticket:", error);
      Swal.error("Failed to download the invoice. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadTicket = async () => {
    try {
      setTicketDownloading(true);

      const element = document.getElementById("ticket-pdf");
      if (!element) return;

      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");

      const pdf = new jsPDF("p", "mm", "a4");

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      const contentWidth = 140;
      const contentHeight = (canvas.height * contentWidth) / canvas.width;

      const x = (pageWidth - contentWidth) / 2;
      const y = (pageHeight - contentHeight) / 2;

      pdf.addImage(imgData, "PNG", x, y, contentWidth, contentHeight);
      pdf.save("ticket.pdf");
    } catch (error) {
      console.error("Download failed:", error);
    } finally {
      setTicketDownloading(false);
    }
  };
  const bookingStatus = getStatusStyle(bookingInfo?.bookingStatus);
  const paymentStatus = getPaymentStatus(bookingInfo?.bookingStatus);

  useEffect(() => {
    const fetchQR = async () => {
      const res = await fetch(bookingInfo.qr_code_link, {
        headers: {
          Authorization: "Bearer YOUR_TOKEN",
        },
      });
      const blob = await res.blob();
      setQrSrc(URL.createObjectURL(blob));
    };

    if (bookingInfo?.qr_code_link) fetchQR();
  }, [bookingInfo]);

  return (
    <div className="ticket-page ">
      {/* <nav className="ticket-breadcrumb" aria-label="Breadcrumb">
        <span>Home</span>
        <ChevronRight />
        <span>Dashboard</span>
        <ChevronRight />
        <span>Book For Others</span>
        <ChevronRight />
        <span>Payment Summary</span>
        <ChevronRight />
        <strong>Booking Information</strong>
      </nav> */}

      <h1 className="ticket-page-title">Booking Information</h1>

      <section className="ticket-shell">
        <button
          type="button"
          className="ticket-back"
          onClick={() => navigate("/booking-list")}
        >
          <ArrowLeft />
          Back
        </button>
        {bookingInfo?.remark_by && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              width: "100%",
            }}
          >
            <div
              style={{
                backgroundColor:
                  bookingInfo?.bookingStatus === "Rejected"
                    ? "#FDECEC"
                    : "#E8F0EA",

                borderRadius: "50px",
                padding: "12px 20px",

                display: "flex",
                alignItems: "center",
                gap: "10px",

                width: "fit-content",
                maxWidth: "100%",
              }}
            >
              {bookingInfo?.bookingStatus === "Rejected" ? (
                <CircleX size={18} color="#DC2626" strokeWidth={2.5} />
              ) : (
                <CircleCheck size={18} color="#4BA35C" strokeWidth={2.5} />
              )}

              <p
                style={{
                  margin: 0,

                  color:
                    bookingInfo?.bookingStatus === "Rejected"
                      ? "#DC2626"
                      : "#4BA35C",

                  fontSize: "12px",
                  fontWeight: 500,
                  lineHeight: 1.3,
                }}
              >
                {bookingInfo?.bookingStatus === "Rejected"
                  ? "Rejected"
                  : "Verified"}
              </p>
            </div>
          </div>
        )}

        <div className="ticket-layout">
          <main className="ticket-main">
            <p className="ticket-eyebrow">Booking Confirmation</p>

            <div className="ticket-venue-row">
              <div className="ticket-venue-copy">
                <h2>{bookingInfo.venueName}</h2>
                <p className="ticket-location-line">
                  <MapPin />
                  {bookingInfo.address}
                </p>
                <a
                  className="ticket-directions"
                  href={bookingInfo.directionsUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  <MapPin />
                  Get Direction
                </a>
              </div>

              <img
                src={bookingInfo.venue_image}
                alt={bookingInfo.venueName}
                className="ticket-ground-image"
              />
            </div>

            <div className="ticket-divider" />

            <h3>Booking Details</h3>
            <dl className="ticket-detail-list">
              <div className="ticket-detail-row">
                <dt>Name</dt>
                <dd>{bookingInfo.name}</dd>
              </div>
              <div className="ticket-detail-row">
                <dt>Contact Number</dt>
                <dd>{bookingInfo.contactNumber}</dd>
              </div>
              <div className="ticket-detail-row">
                <dt>Date</dt>
                <dd>{bookingInfo.date}</dd>
              </div>
              <div className="ticket-detail-row">
                <dt>Sport</dt>
                <dd>{bookingInfo.sport}</dd>
              </div>
              <div className="ticket-detail-row">
                <dt>Field of Play</dt>
                <dd>{bookingInfo.fieldOfPlay}</dd>
              </div>
              <div className="ticket-detail-row">
                <dt>Participants</dt>
                <dd>{bookingInfo.participants}</dd>
              </div>
              <div className="ticket-detail-row">
                <dt>Amount</dt>
                <dd>{bookingInfo.amount}</dd>
              </div>
              <div className="ticket-detail-row">
                <dt>Payment</dt>
                <dd>
                  <span
                    className="px-3 py-1 rounded"
                    style={{
                      fontWeight: 500,
                      fontSize: "14px",
                      backgroundColor: paymentStatus.bg,
                      color: paymentStatus.color,
                    }}
                  >
                    {paymentStatus.label}
                  </span>
                </dd>
              </div>
              <div className="ticket-detail-row">
                <dt>Booking Status</dt>
                <dd>
                  <span
                    className="px-3 py-1 rounded"
                    style={{
                      fontWeight: 500,
                      fontSize: "14px",
                      backgroundColor: bookingStatus.bg,
                      color: bookingStatus.color,
                    }}
                  >
                    {bookingStatus.label}
                  </span>
                </dd>
              </div>
              {(bookingInfo?.bookingStatus === "Completed" ||
                bookingInfo?.bookingStatus === "Verified" ||
                bookingInfo?.bookingStatus === "Rejected"||
              bookingInfo?.bookingStatus === "Canceled") && (
                <div className="ticket-detail-row">
                  <dt>Invoice</dt>
                  <dd>
                    <button
                      type="button"
                      className="ticket-download-small"
                      onClick={handleDownloadInvoice}
                      disabled={loading}
                      style={{
                        opacity: loading ? 0.6 : 1,
                        cursor: loading ? "not-allowed" : "pointer",
                      }}
                    >
                      <Download />
                      {loading ? "Downloading..." : "Download"}
                    </button>
                  </dd>
                </div>
              )}
            </dl>
          </main>

          {(bookingInfo?.bookingStatus === "Completed" ||
            bookingInfo?.bookingStatus === "Verified" ||
            bookingInfo?.bookingStatus === "Rejected" ||
            bookingInfo?.bookingStatus === "Canceled") && (
            <>
              {/* ✅ ONLY THIS PART WILL BE DOWNLOADED */}
              <div>
                <div id="ticket-pdf">
                  <aside className="ticket-pass" aria-label="Booking ticket">
                    <h3>{bookingInfo.venueName}</h3>

                    <p className="ticket-pass-location">
                      <MapPin />
                      {bookingInfo.address}
                    </p>

                    <div className="ticket-booking-id">
                      <span>#Booking ID</span>
                      <strong>{bookingInfo.bookingId}</strong>
                    </div>

                    <div className="ticket-qr-row">
                      {/* <QRCode value={bookingInfo.qr_code_link} size={150} /> */}
                      {bookingInfo?.qr_code_link && (
                        <img src={qrSrc} alt="QR Code" className="ticket-qr" />
                      )}

                      <div className="ticket-mini-details ms-4">
                        <div>
                          <span>Date</span>
                          <strong>{bookingInfo.shortDate}</strong>
                        </div>
                        <div>
                          <span>Time Slot</span>
                          <strong>{bookingInfo.timeSlot}</strong>
                        </div>
                        <div>
                          <span>Sport</span>
                          <strong>{bookingInfo.sport}</strong>
                        </div>
                      </div>
                    </div>

                    <div className="ticket-field-pill">
                      <span>Field of Play:</span>
                      <strong>{bookingInfo.fieldOfPlay}</strong>
                    </div>

                    <div className="ticket-pass-divider" />

                    <div className="ticket-user-row">
                      <img
                        src={bookingInfo.avatar}
                        alt={bookingInfo.name}
                        className="ticket-user-avatar"
                      />
                      <div>
                        <p>{bookingInfo.name}</p>
                        <span style={{ fontSize: "12px" }}>
                          {bookingInfo.email || "N/A"}
                        </span>
                      </div>
                    </div>
                  </aside>
                </div>
                {(bookingInfo?.bookingStatus === "Completed" ||
                  bookingInfo?.bookingStatus === "Verified" ||
                  bookingInfo?.bookingStatus === "Rejected" ||
                  bookingInfo?.bookingStatus === "Canceled") && (
                  <div className="ticket-pass-actions no-print">
                    <button
                      type="button"
                      className="ticket-download-primary"
                      onClick={handleDownloadTicket}
                      disabled={ticketDownloading}
                    >
                      {ticketDownloading ? (
                        "Downloading..."
                      ) : (
                        <>
                          <Download /> Download
                        </>
                      )}
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {openPaymentPopUp && (
          <PaymentPopup
            bookingId={bookingId}
            onClose={() => setOpenPaymentPopup(false)}
            refreshBookingDetails={fetchBookingDetails}
          />
        )}
      </section>

      <div>
        {!bookingInfo?.remark_by &&
          bookingInfo?.bookingStatus == "Completed" && (
            <div className="booking-action-sticky">
              <div className="booking-action-wrapper">
                <button
                  className="booking-btn booking-btn-cancel"
                  onClick={() => navigate("/venue-manager")}
                >
                  Cancel
                </button>

                <button
                  className="booking-btn booking-btn-continue"
                  onClick={() => setOpenPaymentPopup(true)}
                >
                  Continue
                </button>
              </div>
            </div>
          )}
      </div>
    </div>
  );
}
